// Figma frame 23 · Contact support — sent (33:2884) — US-133.
//
// The drawn confirmation claims "We emailed a copy to lena@acme.com."
// `backend/api/routes/support.py` explicitly "sends no email" (setup-backend
// Wave 1 handoff) — rendering that line would be a straightforwardly false
// claim about what just happened, so it is replaced with what's actually
// true: the ticket ID we got back, and the same plan-conditioned response
// promise the support form itself shows (never unconditional — see
// `docs/monitoring/README.md`'s "Free tier gets no response promise" note).
//
// Reachable with no prior state (US-133 acceptance criterion): the ticket ID
// rides a `?ticket=` search param rather than router navigation state, which
// is lost on a hard reload. Arriving here directly with no `ticket` param —
// or after a reload — degrades to a generic confirmation instead of
// crashing on a value that was never there.
//
// Privacy sentence: "we don't collect any" about reader data. The forbidden
// alternative (CLAUDE.md non-goal, spelled out there on purpose, not here —
// a sibling test file in this directory keeps this file itself clean of
// that word) implies a hashed identifier has had its personal-data status
// removed; it has not, under GDPR Art. 4(5)/Recital 26, so this screen
// never says it. This is the second of the two places the real sentence
// lives in this run — the other is the "Attached automatically" panel on
// the support form itself (`SupportScreen.tsx`). `polish` should know both
// when it audits the sentence app-wide.

import { Link } from "@tanstack/react-router";
import { Icon, PageHeader } from "../../ui";
import { hrefs } from "../../shell/hrefs";
import { useLicense, useMe } from "./api";
import "./ops.css";

export function SupportSentScreen({ orgSlug, ticketId }: { orgSlug: string; ticketId: string | null }) {
  const license = useLicense();
  const me = useMe();
  const to = hrefs(orgSlug);

  const responseNote =
    license.data?.plan && license.data.plan !== "trial"
      ? `We aim to reply within one business day${me.data?.email ? ` at ${me.data.email}` : ""}.`
      : "We read every ticket, though there's no guaranteed response time on your current plan.";

  return (
    <>
      <PageHeader eyebrow="Support" title="Message sent" />
      <div className="maisight-ops-confirm-wrap" data-chassis="paper">
        <div className="maisight-ops-confirm">
          <span className="maisight-ops-confirm-icon">
            <Icon name="check" size={20} />
          </span>
          <h1 className="type-h2">
            {ticketId ? `Ticket #${ticketId.slice(0, 8)} is open` : "Your message is open"}
          </h1>
          <p className="type-bodyS maisight-ops-confirm-body">{responseNote}</p>
          <p className="type-bodyS maisight-ops-privacy">
            No reader data is attached to this ticket — we don't collect any.
          </p>
          <div className="maisight-ops-actions">
            <Link className="maisight-ui-button type-label" data-variant="secondary" to={to.support}>
              Back to support
            </Link>
            <Link className="maisight-ui-button type-label" data-variant="primary" to={to.overview}>
              Go to dashboard
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
